/**
 * Card Entry Screen - Telas 2-6
 *
 * Handles card details entry with:
 * - Secure mode OFF/ON states (Telas 2-3)
 * - Progressive masked entry (Telas 4-5)
 * - Manual mode warning (Tela 6)
 */

import type { SecureModeState, CardEntryState } from '@/types/voice-dtmf.types';
import { CardTypeBadge } from '../components/CardTypeBadge';
import { AmountDisplay } from '../components/AmountDisplay';
import { SecureModeBanner } from '../components/SecureModeBanner';
import { useState } from 'react';
import {
  sanitizeCardholderName,
  formatExpiryDate,
  isValidExpiryDate,
  getExpiryError,
} from '@utils/validators';
import styles from './CardEntry.module.css';

interface CardEntryProps {
  amount: number;
  currency: string;
  secureMode: SecureModeState;
  cardEntry?: CardEntryState;
  selectedPaymentMethod?: 'saved-card' | 'new-card';
  manualModeAvailable: boolean;
  isProcessing?: boolean;
  shouldSaveCard?: 'ALWAYS' | 'OPTIONAL' | 'NEVER';
  onFieldChange?: (field: string, value: string) => void;
  onRestart: () => void;
  onCompletePayment: () => void;
  onManualModeClick: () => void;
  onCancel?: () => void;
  cancelDisabled?: boolean;
  /** When true, a cancel is in flight — suppress the PAN/CVV validation UI so a
   *  late SSE FIELD_INVALID/FIELD_PROGRESS event cannot flash an error before the
   *  iframe navigates away (PAS-32798). */
  isCancelling?: boolean;
}

export const CardEntry = ({
  amount,
  currency,
  secureMode,
  cardEntry,
  selectedPaymentMethod,
  manualModeAvailable,
  isProcessing,
  shouldSaveCard = 'NEVER',
  onFieldChange,
  onRestart,
  onCompletePayment,
  onManualModeClick,
  onCancel,
  cancelDisabled,
  isCancelling = false,
}: CardEntryProps) => {
  const [expiryTouched, setExpiryTouched] = useState(false);
  const expiryError = expiryTouched
    ? getExpiryError(cardEntry?.expiryDate ?? '')
    : null;

  // Show invalid state when explicitly rejected OR when all digits entered but no valid confirmation yet
  // Show invalid state when explicitly rejected OR when all digits entered but no valid confirmation yet.
  // While a cancel is in flight, suppress the validation UI entirely — a late SSE
  // event must not flash an error on the fields before the iframe navigates away (PAS-32798).
  const cardNumberShowInvalid =
    !isCancelling &&
    (cardEntry?.cardNumber.isInvalid ||
      cardEntry?.cardNumber.pendingValidation);
  const cvvShowInvalid =
    !isCancelling &&
    (cardEntry?.cvv.isInvalid || cardEntry?.cvv.pendingValidation);

  const isFormComplete = (): boolean => {
    if (!cardEntry) return false;
    return (
      cardEntry.cardholderName.trim() !== '' &&
      cardEntry.cardNumber.isComplete &&
      cardEntry.cvv.isComplete &&
      isValidExpiryDate(cardEntry.expiryDate)
    );
  };

  const displayCardNumber = cardEntry?.cardNumber.isComplete
    ? `**** **** **** ${cardEntry.cardNumber.value.slice(-4)}`
    : cardEntry?.cardNumber.value || '';

  return (
    <div className={styles.container}>
      {/* Amount Display */}
      <AmountDisplay
        amount={amount}
        currency={currency}
        onCancel={onCancel}
        cancelDisabled={cancelDisabled}
      />

      {/* Secure Mode Banner */}
      <SecureModeBanner mode={secureMode.mode} callCode={secureMode.callCode} />

      {/* Card Entry Form */}
      <div className={styles.form}>
        {/* Cardholder Name */}
        <div className={styles.fieldGroup}>
          <label className={styles.label}>Cardholder&apos;s name</label>
          <input
            type="text"
            className={styles.input}
            value={cardEntry?.cardholderName || ''}
            placeholder="Full name as written on card"
            disabled={isProcessing}
            readOnly={selectedPaymentMethod === 'saved-card'}
            onChange={(e) => {
              const sanitizedValue = sanitizeCardholderName(e.target.value);
              onFieldChange?.('cardholderName', sanitizedValue);
            }}
          />
        </div>

        {/* Card Number */}
        <div className={styles.fieldGroup}>
          <label className={styles.label}>
            Card number
            <span className={styles.sensitiveDataBadge}>Sensitive Data</span>
          </label>
          <div className={styles.cardNumberInputWrapper}>
            {cardEntry?.cardNumber.isComplete ? (
              <svg
                className={styles.fieldStatusIconLeft}
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M8 16C12.4183 16 16 12.4183 16 8C16 3.58172 12.4183 0 8 0C3.58172 0 0 3.58172 0 8C0 12.4183 3.58172 16 8 16ZM11.8566 6.19113C12.1002 5.85614 12.0261 5.38708 11.6911 5.14345C11.3561 4.89982 10.8871 4.97388 10.6434 5.30887L7.15969 10.099L5.28033 8.21967C4.98744 7.92678 4.51256 7.92678 4.21967 8.21967C3.92678 8.51256 3.92678 8.98744 4.21967 9.28033L6.71967 11.7803C6.87477 11.9354 7.08999 12.0149 7.30867 11.9977C7.52734 11.9805 7.72754 11.8685 7.85655 11.6911L11.8566 6.19113Z"
                  fill="#059669"
                />
              </svg>
            ) : (
              <svg
                className={styles.fieldStatusIconLeft}
                width="14"
                height="18"
                viewBox="0 0 14 18"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M7 0C9.48528 0 11.5 2.01472 11.5 4.5V8H12C13.1046 8 14 8.89543 14 10V16C14 17.1046 13.1046 18 12 18H2C0.895431 18 0 17.1046 0 16V10C0 8.89543 0.895431 8 2 8H2.5V4.5C2.5 2.01472 4.51472 0 7 0ZM7 1.5C5.34315 1.5 4 2.84315 4 4.5V8H10V4.5C10 2.84315 8.65685 1.5 7 1.5Z"
                  fill="#71716F"
                />
              </svg>
            )}
            <input
              type="text"
              data-testid="card-number-input"
              className={`${styles.input} ${styles.inputWithIcon}${cardNumberShowInvalid ? ` ${styles.inputError}` : ''}`}
              value={displayCardNumber}
              placeholder="Debit, credit or prepaid cards"
              disabled={
                (secureMode.mode === 'off' && !cardEntry?.cardNumber.value) ||
                isProcessing
              }
              readOnly
            />
            {cardNumberShowInvalid && (
              <svg
                data-testid="card-number-error-icon"
                className={styles.fieldWarningIconRight}
                width="18"
                height="15"
                viewBox="0 0 18 15"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M7.4845 1.49499C8.15808 0.328327 9.84201 0.32833 10.5156 1.495L16.7943 12.37C17.4678 13.5367 16.6259 14.995 15.2787 14.995H2.72136C1.37421 14.995 0.532242 13.5367 1.20582 12.37L7.4845 1.49499ZM9 4C9.41421 4 9.75 4.33579 9.75 4.75V8.25C9.75 8.66421 9.41421 9 9 9C8.58579 9 8.25 8.66421 8.25 8.25L8.25 4.75C8.25 4.33579 8.58579 4 9 4ZM9 13C9.55228 13 10 12.5523 10 12C10 11.4477 9.55228 11 9 11C8.44772 11 8 11.4477 8 12C8 12.5523 8.44772 13 9 13Z"
                  fill="#B91C1C"
                />
              </svg>
            )}
            {cardEntry?.cardType && !cardNumberShowInvalid && (
              <div
                className={styles.cardTypeBadge}
                data-testid="card-type-badge"
              >
                <CardTypeBadge cardType={cardEntry.cardType} size="small" />
              </div>
            )}
          </div>
          {cardNumberShowInvalid && (
            <span className={styles.fieldError}>
              Please enter a valid card number
            </span>
          )}
        </div>

        {/* CVV */}
        <div className={styles.fieldGroup}>
          <label className={styles.label}>
            CVV
            <span className={styles.sensitiveDataBadge}>Sensitive Data</span>
          </label>
          <div className={styles.cvvInputWrapper}>
            {cardEntry?.cvv.isComplete ? (
              <svg
                className={styles.fieldStatusIconLeft}
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M8 16C12.4183 16 16 12.4183 16 8C16 3.58172 12.4183 0 8 0C3.58172 0 0 3.58172 0 8C0 12.4183 3.58172 16 8 16ZM11.8566 6.19113C12.1002 5.85614 12.0261 5.38708 11.6911 5.14345C11.3561 4.89982 10.8871 4.97388 10.6434 5.30887L7.15969 10.099L5.28033 8.21967C4.98744 7.92678 4.51256 7.92678 4.21967 8.21967C3.92678 8.51256 3.92678 8.98744 4.21967 9.28033L6.71967 11.7803C6.87477 11.9354 7.08999 12.0149 7.30867 11.9977C7.52734 11.9805 7.72754 11.8685 7.85655 11.6911L11.8566 6.19113Z"
                  fill="#059669"
                />
              </svg>
            ) : (
              <svg
                className={styles.fieldStatusIconLeft}
                width="14"
                height="18"
                viewBox="0 0 14 18"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M7 0C9.48528 0 11.5 2.01472 11.5 4.5V8H12C13.1046 8 14 8.89543 14 10V16C14 17.1046 13.1046 18 12 18H2C0.895431 18 0 17.1046 0 16V10C0 8.89543 0.895431 8 2 8H2.5V4.5C2.5 2.01472 4.51472 0 7 0ZM7 1.5C5.34315 1.5 4 2.84315 4 4.5V8H10V4.5C10 2.84315 8.65685 1.5 7 1.5Z"
                  fill="#71716F"
                />
              </svg>
            )}
            <input
              type="text"
              data-testid="cvv-input"
              className={`${styles.input} ${styles.inputWithIcon}${cvvShowInvalid ? ` ${styles.inputError}` : ''}`}
              value={cardEntry?.cvv.value || ''}
              placeholder="Customer card security code"
              disabled={
                (secureMode.mode === 'off' && !cardEntry?.cvv.value) ||
                isProcessing
              }
              readOnly
            />
            {cvvShowInvalid && (
              <svg
                data-testid="cvv-error-icon"
                className={styles.fieldWarningIconRight}
                width="18"
                height="15"
                viewBox="0 0 18 15"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M7.4845 1.49499C8.15808 0.328327 9.84201 0.32833 10.5156 1.495L16.7943 12.37C17.4678 13.5367 16.6259 14.995 15.2787 14.995H2.72136C1.37421 14.995 0.532242 13.5367 1.20582 12.37L7.4845 1.49499ZM9 4C9.41421 4 9.75 4.33579 9.75 4.75V8.25C9.75 8.66421 9.41421 9 9 9C8.58579 9 8.25 8.66421 8.25 8.25L8.25 4.75C8.25 4.33579 8.58579 4 9 4ZM9 13C9.55228 13 10 12.5523 10 12C10 11.4477 9.55228 11 9 11C8.44772 11 8 11.4477 8 12C8 12.5523 8.44772 13 9 13Z"
                  fill="#B91C1C"
                />
              </svg>
            )}
          </div>
          {cvvShowInvalid && (
            <span className={styles.fieldError}>
              Please enter a valid security code
            </span>
          )}
        </div>

        {/* Expiration Date */}
        <div className={styles.fieldGroup}>
          <label className={styles.label}>Expiration date</label>
          <input
            type="text"
            className={`${styles.input}${expiryError ? ` ${styles.inputError}` : ''}`}
            value={cardEntry?.expiryDate || ''}
            placeholder="mm/yy"
            disabled={isProcessing}
            readOnly={selectedPaymentMethod === 'saved-card'}
            maxLength={5}
            onChange={(e) =>
              onFieldChange?.('expiryDate', formatExpiryDate(e.target.value))
            }
            onBlur={() => setExpiryTouched(true)}
          />
          {expiryError && (
            <span className={styles.fieldError}>{expiryError}</span>
          )}
        </div>

        {/* Save Card Checkbox — only shown when customer.reference was provided at session init */}
        {selectedPaymentMethod === 'new-card' &&
          shouldSaveCard === 'OPTIONAL' && (
            <div className={styles.saveCardOption}>
              <input
                type="checkbox"
                id="save-card"
                checked={cardEntry?.saveCard || false}
                onChange={(e) =>
                  onFieldChange?.('saveCard', String(e.target.checked))
                }
                disabled={isProcessing}
              />
              <label htmlFor="save-card">Save card for future payments</label>
            </div>
          )}
      </div>

      {/* Action Buttons */}
      <div className={styles.actions}>
        <button
          type="button"
          className={styles.restartButton}
          onClick={onRestart}
          disabled={isProcessing}
        >
          Reset sensitive data
          <svg
            width="14"
            height="16"
            viewBox="0 0 14 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M12.0998 9.24462C11.3136 12.1787 8.29778 13.9199 5.36372 13.1337C4.3947 12.8741 3.55765 12.3724 2.89793 11.7115L2.58639 11.3999L5.01963 11.3995C5.43384 11.3994 5.76957 11.0636 5.76949 10.6494C5.76942 10.2351 5.43357 9.89942 5.01936 9.8995L0.775924 9.90026C0.577008 9.90029 0.386254 9.97935 0.245626 10.12C0.104998 10.2607 0.0260171 10.4515 0.0260594 10.6504L0.026951 14.8922C0.0270379 15.3065 0.362895 15.6422 0.777108 15.6421C1.19132 15.642 1.52704 15.3061 1.52695 14.8919L1.52644 12.4613L1.83629 12.7712C2.67733 13.6137 3.74501 14.2529 4.97549 14.5826C8.70975 15.5832 12.5481 13.3671 13.5487 9.63285C13.6559 9.23275 13.4185 8.8215 13.0184 8.71429C12.6183 8.60708 12.207 8.84452 12.0998 9.24462ZM13.3287 5.52223C13.4694 5.38156 13.5484 5.19076 13.5484 4.99183L13.5477 0.749877C13.5476 0.335664 13.2118 -6.71362e-05 12.7975 1.00702e-08C12.3833 6.90604e-05 12.0476 0.33591 12.0477 0.750123L12.0481 3.18099L11.7382 2.87118C10.8973 2.02876 9.82933 1.38931 8.59896 1.05963C4.86469 0.0590416 1.02633 2.27512 0.0257418 6.00938C-0.0814646 6.40948 0.155972 6.82073 0.556072 6.92794C0.956171 7.03515 1.36742 6.79771 1.47463 6.39761C2.26081 3.46355 5.27666 1.72234 8.21073 2.50852C9.17979 2.76818 10.0169 3.26985 10.6766 3.93083L10.9877 4.24195L8.55572 4.24195C8.14151 4.24195 7.80572 4.57773 7.80572 4.99195C7.80572 5.40616 8.14151 5.74195 8.55572 5.74195H12.7984C12.9973 5.74195 13.1881 5.66291 13.3287 5.52223Z"
              fill="currentColor"
            />
          </svg>
        </button>
        <button
          type="button"
          className={styles.completeButton}
          disabled={!isFormComplete() || isProcessing}
          onClick={onCompletePayment}
        >
          {isProcessing ? (
            <>
              <svg
                className={styles.spinner}
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle
                  cx="8"
                  cy="8"
                  r="6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeDasharray="8 24"
                />
              </svg>
              Processing...
            </>
          ) : (
            'Complete Payment'
          )}
        </button>
      </div>
      {/* Manual Mode Warning */}
      {manualModeAvailable && (
        <div className={styles.manualModeWarning}>
          <div className={styles.warningText}>
            To input sensitive card data manually, enable{' '}
            <button
              type="button"
              className={styles.manualModeLink}
              onClick={onManualModeClick}
            >
              manual mode
            </button>
            .
          </div>
          <div className={styles.warningSubtext}>
            Please note, usage of manual mode will be audited for adherence
            policy.
          </div>
        </div>
      )}
    </div>
  );
};
